import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Player, { Config } from 'react-player';
import { TemplateClassNames } from '../../../../types/template-layouts';
import { HomepageVideo } from '../../../../types/working-model';
import { Button } from '../../../general-ui/button/button';
import Icon, { COLORS, ICONS } from '../../../general-ui/icon';
import { EIcon } from '../../../general-ui/icon/icon';
import WaitingIndicator from '../../../general-ui/waiting-indicator/waiting-indicator';
import './video.scss';
import { Level } from 'hls.js';
import { Popover } from '../../../general-ui/popover/popover';
interface Props {
	video?: HomepageVideo;
	url?: string;
	template: TemplateClassNames;
	multi?: boolean;
	main?: boolean;
	onRequestFullscreen?: (fullscreen: boolean) => void;
	isFullscreen?: boolean;
	chatClosed?: boolean;
	onChatClose?: (chatClosed: boolean) => void;
	hideChatButton?: boolean;
	isOnDemand?: boolean;
	isMain?: boolean;
	currentlyWatching?: string | null;
}


export default function Video(props: Props): JSX.Element {
	const {
		video,
		template,
		main,
		url,
		onRequestFullscreen,
		isFullscreen,
		chatClosed,
		onChatClose,
		hideChatButton = false,
		isOnDemand = false,
		isMain = true,
		currentlyWatching
	} = props;

	const [showVid, setShowVid] = useState(false);

	//only autoplay the main video
	const [playing, setPlaying] = useState(isMain);
	const [progress, setProgress] = useState(0);
	const [loaded, setLoaded] = useState(0);
	const [muted, setMuted] = useState(false);
	const [dragging, setDragging] = useState(false);
	const [dragLeft, setDragLeft] = useState(0);
	const [buffering, setBuffering] = useState(false);
	const [levels, setLevels] = useState<Level[]>([]);
	const [hasHls, setHasHls] = useState(false);
	const [hideControls, setHideControls] = useState(false);
	const controlHideTimeout = useRef<NodeJS.Timeout | null>(null);

	// level of -1 is auto
	const [currentLevel, setCurrentLevel] = useState<number>(-1);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const startX = useRef(0);
	const deltaX = useRef(0);
	const correctedX = useRef(0);
	const track = useRef<HTMLDivElement | null>(null);
	const player = useRef<Player | null>(null);
	const hls = useRef<any>(null);

	function revealVideo() {
		setShowVid(true);
		setPlaying(true);
	}

	const onProgress = useCallback(({ played, loaded }: { played: number, loaded: number }) => {
		setProgress(played * 100);
		setLoaded(loaded * 100);
	}, []);

	const showMp4Controls = useMemo(() => {
		return url || video?.source?.endsWith('.mp4') || video?.hls_link;
	}, [video, url]);

	const seekMouseDown = useCallback((e: any) => {
		startX.current = e.touches ? e.touches[0].clientX : e.clientX;
		setDragging(true);
	}, []);

	const onBuffer = useCallback(() => {
		setBuffering(true);

		//onBufferEnd sometimes never gets called on iOS - just hard turn it off after 10 seconds if it's not fired by then
		setTimeout(() => {
			if (buffering) {
				setBuffering(false);
			}
		}, 10000);
	}, [buffering]);

	const onBufferEnd = useCallback(() => {
		setBuffering(false);
	}, []);

	useEffect(() => {
		const handleMouseMove = (e: any) => {
			const clientX = e.touches ? e.touches[0].clientX : e.clientX;
			const { left } = track.current!.getBoundingClientRect();
			deltaX.current = clientX - startX.current;
			correctedX.current = clientX - left;
			setDragLeft(correctedX.current);
		};

		const handleMouseUp = () => {
			setDragging(false);
			const { width } = track.current!.getBoundingClientRect();
			const percentMoved = correctedX.current / width;
			player.current!.seekTo(Math.abs(percentMoved), "fraction");
		};

		if (dragging) {
			window.addEventListener('mousemove', handleMouseMove);
			window.addEventListener('touchmove', handleMouseMove);
			window.addEventListener('mouseup', handleMouseUp);
			window.addEventListener('touchend', handleMouseUp);
		}

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('touchmove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
			window.removeEventListener('touchend', handleMouseUp);
		};
	}, [dragging]);

	useEffect(() => {
		const handleOrientationChange = () => {
			onRequestFullscreen?.(window.screen.orientation?.type.startsWith('landscape'));
		};

		window.addEventListener('orientationchange', handleOrientationChange);

		return () => {
			window.removeEventListener('orientationchange', handleOrientationChange);
		};
	}, [onRequestFullscreen]);

	const getInternalPlayer = useCallback(() => {
		//uses HLS JS to play recorded/live videos
		hls.current = player.current?.getInternalPlayer('hls');

		if (hls.current) {
			setHasHls(true);

			//levels are available resolutions
			setLevels(hls.current.levels);

			//hard set current level to auto
			hls.current.currentLevel = -1;
		}
	}, []);

	const toggleMuted = useCallback(() => {
		setMuted(!muted);
	}, [muted]);

	const togglePlaying = useCallback(() => {
		setPlaying(!playing);
	}, [playing]);

	const toggleSettings = useCallback(() => {
		setSettingsOpen(true);
	}, []);

	const popoverClosed = useCallback(() => {
		setSettingsOpen(false);
	}, []);

	const setResolution = useCallback((level: number) => {
		return () => {
			hls.current.currentLevel = level;
			setCurrentLevel(level);
		};
	}, []);

	const toggleFullscreen = useCallback(() => {
		onRequestFullscreen!(!isFullscreen);
	}, [isFullscreen, onRequestFullscreen]);

	//when user clicks on a spot on the progress bar, seek to that spot
	const handleProgressClick = useCallback((e: React.MouseEvent) => {
		const { left, width } = track.current!.getBoundingClientRect();
		correctedX.current = e.clientX - left;
		const percentMoved = correctedX.current / width;
		player.current!.seekTo(Math.abs(percentMoved), "fraction");
	}, []);

	const toggleChatClose = useCallback(() => {
		onChatClose?.(!chatClosed);
	}, [chatClosed, onChatClose]);

	const handleEnded = useCallback(() => {
		setPlaying(false);
	}, []);

	const handleError = useCallback((e: Error) => {
		if (e.name === "NotAllowedError" || e.name === 'AbortError') {
			console.warn("Autoplay Blocked by user setting.");
			setPlaying(false);
		}
	}, []);

	const handlePlay = useCallback(() => {
		if (!playing) setPlaying(true);

		if (controlHideTimeout.current) clearTimeout(controlHideTimeout.current);

		controlHideTimeout.current = setTimeout(() => {
			if (playing) {
				setHideControls(true);
			}
		}, 2000);
	}, [playing]);

	const handleTap = () => {
		setHideControls(false);
		if (controlHideTimeout.current) clearTimeout(controlHideTimeout.current);

		controlHideTimeout.current = setTimeout(() => {
			if (playing) {
				setHideControls(true);
			}
		}, 2000);
	};


	// some videos from youtube wont play in this player on deployed sites
	const playerConfig: Config = {
		youtube: {
			playerVars: {
				origin: `${process.env.PUBLIC_URL}`,
				enablejsapi: 1,
			}
		}
	};

	return (
		<div className={classNames(main ? `video-main ${template}` : `video-preview ${template}`)} onClick={handleTap}>
			{showVid || !video?.use_thumbnail ? (
				<>
					{video?.type === 'embed' ? (
						<div className='embed-wrapper' dangerouslySetInnerHTML={{ __html: video.source! }} />
					) : (
						<>
							{(video?.source || !!url) && (
								<>
									<Player
										url={url ? url : (video!.hls_link ? video!.hls_link : video!.source!)}
										height="100%"
										width="100%"
										playing={playing}
										style={{ background: '#000' }}
										muted={muted}
										onError={handleError}
										onProgress={onProgress}
										onBuffer={onBuffer}
										onBufferEnd={onBufferEnd}
										onPlay={handlePlay}
										ref={player}
										onStart={getInternalPlayer}
										onEnded={handleEnded}
										playsinline={true}
										controls={!showMp4Controls}
										config={playerConfig}
									/>
									{currentlyWatching && (
										<div className="currently-watching"><Icon name={ICONS.VIEWERS_EYE} color={COLORS.WHITE} size={16} /> {currentlyWatching !== "0" ? currentlyWatching : ''}</div>
									)}
									{showMp4Controls && (
										<div className={classNames("player-controls", { isFullscreen, hideControls })}>
											<Button
												classButton={classNames('player-button primary')}
												template={template}
												onClick={togglePlaying}
											>
												{
													playing
														? <EIcon name="pause" size="medium" />
														: <EIcon name="play" size="medium" />
												}
											</Button>

											{main && (
												<Button
													classButton={classNames('player-button mute')}
													template={template}
													onClick={toggleMuted}
												>
													{
														muted ? (
															<Icon name={ICONS.MUTED} size={22} color={COLORS.WHITE} />
														) : (
															<Icon name={ICONS.MUTE} size={22} color={COLORS.WHITE} />
														)
													}
												</Button>
											)}

											{
												(isOnDemand || !isMain) && (
													<div className="progress-wrapper">
														<div className="progress-container">
															<div className="progress-track" ref={track} onClick={handleProgressClick}>
																<div className="progress-loaded" style={{ width: `${loaded}%` }} />
																<div className="progress-played" style={{ width: `${progress}%` }} />
																<div
																	onMouseDown={seekMouseDown}
																	onTouchStart={seekMouseDown}
																	className="progress-thumb"
																	style={{
																		left: dragging ? dragLeft : `${progress}%`,
																		cursor: dragging ? 'grabbing' : 'grab'
																	}}
																/>
															</div>
														</div>
													</div>
												)
											}

											{buffering && (
												<div className="buffering-container">
													<WaitingIndicator />
												</div>
											)}

											{hasHls && (
												<Button
													classButton={classNames('player-button settings')}
													template={template}
													onClick={toggleSettings}
												>
													<Icon name={ICONS.SETTINGS} size={22} color={COLORS.WHITE} />

													<Popover bottom={true} open={settingsOpen} onClose={popoverClosed}>
														<ul className="levels">
															{levels?.map((level: Level, index: number) => (
																<li
																	key={level.height}
																	onClick={setResolution(index)}
																	className={classNames({ selected: currentLevel === index })}
																>
																	{level.height}p
																</li>
															))}
															<li className={classNames({ selected: currentLevel === -1 })} onClick={setResolution(-1)}>Auto</li>
														</ul>
													</Popover>
												</Button>
											)}

											{onRequestFullscreen && (
												<Button
													onClick={toggleFullscreen}
													classButton={classNames('player-button fullscreen')}
													template={template}
												>
													{!isFullscreen ? (
														<Icon name={ICONS.FULLSCREEN} size={22} color={COLORS.WHITE} />
													) : (
														<Icon name={ICONS.CLOSE} size={22} color={COLORS.WHITE} />
													)}
												</Button>
											)}

											{!hideChatButton && isMain && (
												<Button
													onClick={toggleChatClose}
													classButton={classNames('player-button chat-close')}
													template={template}
												>
													{
														chatClosed ? (
															<Icon name={ICONS.CHAT_COMMENT} size={22} color={COLORS.WHITE} />
														) : (
															<Icon name={ICONS.CHAT_HIDE} size={22} color={COLORS.WHITE} />
														)
													}
												</Button>
											)}
										</div>
									)}
								</>
							)}
						</>
					)}

				</>
			) : (
				<div
					className="video-poster"
					style={{ backgroundImage: `url(${video.image})` }}
				>
					<Button
						classButton={classNames('play-button primary')}
						template={template}
						onClick={revealVideo}
					>
						<EIcon name="play" size="medium" />
					</Button>
				</div>
			)}
		</div>
	);
}
